import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import * as React from 'react';
import { Navigate } from 'react-router-dom';

import { useFeatureGate, useFeatureGates } from './hooks/useFeatureGates';
import { PATHS } from './paths';
import { withAssistantSheet } from './providers/assistantSheetHistory';
import { useSidePanel } from './providers/SidePanelProvider';

/**
 * `/alerts` has no body of its own: the Notification Center is a layer of the
 * assistant pane at every width, so the route opens the full center and
 * redirects to wherever the pane is — the dashboard at `xl+`, where the side
 * column shows it; `/assistant` between `md` and `xl`, where the pane is the
 * route; and the dashboard again below `md`, carrying the history state that
 * opens the sheet there. A viewer without `feat-alerts-center` has no center
 * to open and lands on the dashboard.
 *
 * This is the deep-link path only — a bookmark, a new tab, a modified click on
 * the bell. A plain bell click opens the center in place and never comes here,
 * which is why checking an alert does not replace the pane the user was
 * reading.
 *
 * `replace` is what keeps Back working: a pushed `/alerts` entry would re-enter
 * this gate on every Back press and trap the user going forward. Once the
 * center is open, the chevron and Escape are the way out of it.
 */
const AlertsRouteGate = () => {
  const enabled = useFeatureGate(FeatureGate.AlertsCenter);
  const { loading, resolved } = useFeatureGates();
  const { assistantMountSite, showAlerts, openAlertsCenter } = useSidePanel();

  // A layout effect, so the panel is already showing the center by the time
  // `<Navigate>`'s passive effect fires and unmounts this gate. On the sheet
  // only the card moves here: the sheet itself opens through the redirect's
  // state, and `showAlerts` would push a second entry on `/alerts` first.
  React.useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    if (assistantMountSite === 'sheet') {
      openAlertsCenter();
    } else {
      showAlerts();
    }
  }, [enabled, assistantMountSite, showAlerts, openAlertsCenter]);

  if (loading && !resolved) {
    return null;
  }
  if (!enabled) {
    return <Navigate to={PATHS.DASHBOARD} replace />;
  }
  if (assistantMountSite === 'sheet') {
    return <Navigate to={PATHS.DASHBOARD} replace state={withAssistantSheet(null, 'replaced')} />;
  }
  return <Navigate to={assistantMountSite === 'column' ? PATHS.DASHBOARD : PATHS.ASSISTANT} replace />;
};

export default AlertsRouteGate;
